import { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Flex, Button, Select, toast } from "@jsq/ds/core";
import { client } from "~/apollo/client";
import {
  ComplianceCaseAction as CaseActions,
  ComplianceCaseTypeEnum as CaseTypeEnum,
  ComplianceCaseStatus as CaseStatusEnum,
} from "~/graphql/supergraphEnums";
import { usePerformActionMutation } from "~/graphql/supergraphHooks";
import {
  type ComplianceCaseAction,
  type ComplianceCaseStatus,
  type ComplianceCaseSubStatus,
  type ComplianceCaseTypeEnum as ComplianceCaseTypeEnumRawType,
  type GetComplianceCaseQuery,
} from "~/graphql/supergraphTypes";
import { useComplianceEntityOnboardingEnabled } from "../../../flags";
import {
  ESCALATION_REVAMP_CASE_TYPES,
  isInternalEscalationSource,
} from "../../../utils/constants";
import CreateEscalationModal from "../../Escalations/CreateEscalationModal";
import { escalationTakeoverPaths } from "../../Escalations/EscalationTakeover/paths";
import { EscalationsMenu } from "../EscalationsMenu";
import {
  getEscalationsMenuFlags,
  shouldRenderEscalationsMenu,
} from "../EscalationsMenu/escalationsMenuUtils";
import { RelatedCasesPill } from "../RelatedCasesPill";
import { disabledOptionText, makerControls, smallMargin } from "../styles_vanilla.css";
import { WAIVER_REQUIRED_TOOLTIP, ESCALATION_REQUIRED_TOOLTIP } from "../Tooltips/constants";
import DisabledConclusionTooltip from "../Tooltips/DisabledConclusionTooltip";
import AddCommentModal from "./AddCommentModal";
import {
  MarkSuccessKey,
  NEW_ACTION_LABEL_CASE_TYPES,
  getLabel,
  getInternalActionLabel,
  getInternalMarkSuccessLabel,
  getMarkSuccessLabel,
  getNewActionLabel,
  gpCaseTypes,
  isNewActionKey,
} from "./utils";

type Props = {
  caseId: number;
  actions: string[];
  caseStatus: ComplianceCaseStatus | undefined | null;
  caseSubstatus: ComplianceCaseSubStatus | undefined | null;
  caseType: ComplianceCaseTypeEnumRawType;
  escalations?: NonNullable<GetComplianceCaseQuery["complianceCase"]>["escalations"];
  profilesForCase?: GetComplianceCaseQuery["complianceCase"] | null | undefined;
  onSubmit: (submitAction: () => void) => void;
  caseResolution?: string | null | undefined;
};

type EscalationType = NonNullable<
  NonNullable<GetComplianceCaseQuery["complianceCase"]>["escalations"]
>[0];

const MakerControlsSelectMenu = ({
  caseId,
  actions,
  caseStatus,
  caseSubstatus,
  caseType,
  escalations,
  profilesForCase,
  onSubmit,
  caseResolution,
}: Props) => {
  const [selectValue, setSelectValue] = useState<ComplianceCaseAction | "">("");
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [isEditEscalationOpen, setIsEditEscalationOpen] = useState(false);
  const [selectedEscalationId, setSelectedEscalationId] = useState<number | null>(null);

  const selectedEscalation: EscalationType | undefined = useMemo(() => {
    if (!escalations || selectedEscalationId == null) return undefined;
    return escalations.find((e) => (e?.id as number | undefined) === selectedEscalationId);
  }, [escalations, selectedEscalationId]);

  // currently only used by investor onboarding - potentially make a rule for this on the BE instead.
  const [performActionMutation, { loading }] = usePerformActionMutation({
    refetchQueries: [
      "GetComplianceCase",
      "GetComplianceCaseAuditLogs",
      "GetComplianceCasesSlice",
    ],
    onCompleted: async () => {
      toast.success(
        (internalEscalationsEnabled &&
          getInternalMarkSuccessLabel(selectValue, caseType, sourceCaseType)) ||
          getMarkSuccessLabel(selectValue as MarkSuccessKey),
      );
      await client.refetchQueries({
        include: "active",
      });
    },
    onError: (e) => {
      if (e instanceof Error) {
        toast.error(e.message);
      } else {
        toast.error("An unknown error occurred");
      }
    },
  });

  const actionsWithCommentModal: ComplianceCaseAction[] = [
    CaseActions.Accept,
    CaseActions.Reject,
    CaseActions.ProposeAcceptance,
    CaseActions.ProposeRejection,
  ];

  const onClick = () => {
    if (gpCaseTypes.includes(caseType)) {
      setShowSubmitModal(true);
      return;
    }

    if (selectValue === "") {
      return;
    }

    if (actionsWithCommentModal.includes(selectValue)) {
      setShowSubmitModal(true);
      return;
    }

    onSubmit(() =>
      performActionMutation({
        variables: {
          caseId: caseId,
          action: selectValue,
        },
      }),
    );
  };

  const onChange = (value: string | number | string[] | number[]) => {
    // value is always a string, TypeScript error with onChange handler in Select
    setSelectValue(value as ComplianceCaseAction);
  };

  const history = useHistory();
  const internalEscalationsEnabled = useComplianceEntityOnboardingEnabled();
  const sourceCaseType = profilesForCase?.sourceCase?.caseType as CaseTypeEnum | undefined;
  const isEscalationCase = caseType === CaseTypeEnum.Escalation;
  const waiverDocMissing = (escalations?.[0]?.waiverDocumentId ?? null) == null;
  const sourceIsIoOrPicr =
    !!sourceCaseType && ESCALATION_REVAMP_CASE_TYPES.includes(sourceCaseType);
  const skipWaiverForInternal =
    internalEscalationsEnabled && isInternalEscalationSource(sourceCaseType);
  const disableForEscalationSource =
    isEscalationCase && sourceIsIoOrPicr && !skipWaiverForInternal;
  const disableForWaiverMissing = disableForEscalationSource && waiverDocMissing;

  useEffect(() => {
    const escalationSelectionSelected =
      selectValue === CaseActions.ProposeEscalation ||
      selectValue === CaseActions.ProposeFundEscalation;
    const selectionNowDisabled =
      escalationSelectionSelected && (!escalations || escalations.length === 0);
    if (selectionNowDisabled) {
      setSelectValue("");
    }
  }, [escalations, selectValue]);

  const usesNewActionLabel = NEW_ACTION_LABEL_CASE_TYPES.includes(caseType as CaseTypeEnum);

  const sortByReturnToMaker = (a: string, b: string) =>
    a === CaseActions.ReturnToMaker ? 1 : b === CaseActions.ReturnToMaker ? -1 : 0;

  return (
    <Flex className={makerControls}>
      {isEditEscalationOpen && selectedEscalation && (
        <CreateEscalationModal
          show={isEditEscalationOpen}
          onCloseModal={() => setIsEditEscalationOpen(false)}
          caseType={caseType}
          profileId={profilesForCase?.investingProfile?.id ?? 0}
          caseStatus={caseStatus ?? CaseStatusEnum.Ready}
          profilesForCase={profilesForCase}
          mode="edit"
          escalationToEdit={selectedEscalation}
        />
      )}
      <RelatedCasesPill
        currentCaseId={caseId}
        investingProfileId={profilesForCase?.investingProfile?.id}
        caseIsClosed={false}
        className={smallMargin}
      />
      {shouldRenderEscalationsMenu(caseType, escalations, internalEscalationsEnabled) && (
        <div className={smallMargin}>
          <EscalationsMenu
            caseId={caseId}
            escalations={escalations}
            profilesForCase={profilesForCase}
            {...getEscalationsMenuFlags(caseResolution, caseId, profilesForCase)}
            editMode={true}
            onSelect={(id) => {
              if (internalEscalationsEnabled) {
                history.push(escalationTakeoverPaths.edit(caseId, id), {
                  from: history.location.pathname,
                });
                return;
              }
              setSelectedEscalationId(id);
              setIsEditEscalationOpen(true);
            }}
          />
        </div>
      )}
      {(() => {
        const resolutionSelect = (
          <Select
            placeholder="Select resolution"
            position="up"
            value={selectValue}
            onChange={onChange}
            className={smallMargin}
            disabled={disableForWaiverMissing}
          >
            <Select.Section>
              {actions
                .filter(
                  (action) => action !== CaseActions.Unassign && action !== CaseActions.Assign,
                )
                .sort(sortByReturnToMaker)
                .map((option, index) => {
                  const isEscalationSelection =
                    option === CaseActions.ProposeEscalation ||
                    option === CaseActions.ProposeFundEscalation;
                  const isOptionDisabled =
                    isEscalationSelection && (!escalations || escalations.length === 0);
                  const labelText =
                    (internalEscalationsEnabled &&
                      getInternalActionLabel(option, caseType, sourceCaseType)) ||
                    (usesNewActionLabel && isNewActionKey(option)
                      ? getNewActionLabel(option, caseType)
                      : getLabel(option, caseStatus, caseSubstatus));
                  return (
                    <Select.Option value={option} key={index} disabled={isOptionDisabled}>
                      {isOptionDisabled ? (
                        <DisabledConclusionTooltip message={ESCALATION_REQUIRED_TOOLTIP}>
                          <div className={disabledOptionText}>{labelText}</div>
                        </DisabledConclusionTooltip>
                      ) : (
                        labelText
                      )}
                    </Select.Option>
                  );
                })}
            </Select.Section>
          </Select>
        );
        if (disableForWaiverMissing) {
          return (
            <DisabledConclusionTooltip message={WAIVER_REQUIRED_TOOLTIP}>
              <div>{resolutionSelect}</div>
            </DisabledConclusionTooltip>
          );
        }
        return resolutionSelect;
      })()}

      <Button
        className={smallMargin}
        variant="primary"
        onClick={onClick}
        disabled={disableForWaiverMissing || selectValue == "" || loading}
      >
        Submit for review
      </Button>

      <AddCommentModal
        caseId={caseId}
        selectValue={selectValue}
        onClose={() => setShowSubmitModal(false)}
        showSubmitModal={showSubmitModal}
        onSubmit={onSubmit}
      />
    </Flex>
  );
};

export default MakerControlsSelectMenu;
